import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Gauge, CalendarClock, Activity, AlertTriangle, Fuel, Droplet, Container, CircleDot, Lightbulb, Wrench, MoreHorizontal } from "lucide-react";
import api from "../lib/api";
import { ACTION_TYPES } from "../lib/constants";

const ICONS = { AlertTriangle, Fuel, Droplet, Container, CircleDot, Lightbulb, Wrench, MoreHorizontal };
const STATO_BADGE = {
  OK: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  INOP: "bg-red-500/10 text-red-400 border-red-500/30",
  DISMESSO: "bg-slate-500/10 text-slate-400 border-slate-500/30",
};

export default function VehiclePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/vehicles/${id}`).then(({ data }) => setV(data)).catch(() => setErr("Mezzo non trovato."));
  }, [id]);

  if (err)
    return (
      <div className="max-w-md mx-auto min-h-screen flex flex-col items-center justify-center gap-4 p-6">
        <p className="text-slate-400">{err}</p>
        <button onClick={() => navigate("/")} className="text-blue-400 underline">Torna alla home</button>
      </div>
    );
  if (!v) return <div className="min-h-screen grid place-items-center text-slate-500">Caricamento...</div>;

  const ultimo = v.ultimo_intervento ? new Date(v.ultimo_intervento).toLocaleDateString("it-IT") : "—";

  return (
    <div className="max-w-md mx-auto min-h-screen px-4 pt-4 pb-16">
      <header className="flex items-center gap-2 py-2 mb-2">
        <button onClick={() => navigate("/")} className="p-2 -ml-2 rounded-lg hover:bg-slate-800" data-testid="vehicle-back-btn">
          <ChevronLeft className="w-6 h-6" />
        </button>
        <span className="font-head font-semibold">Scheda Mezzo</span>
      </header>

      <div className="relative rounded-2xl p-5 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 border border-slate-700/80 shadow-xl overflow-hidden fade-up" data-testid="vehicle-card">
        <div className="absolute -right-6 -top-6 w-28 h-28 bg-blue-500/10 rounded-full blur-2xl" />
        <div className="flex items-start justify-between">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-400">MEZZO {v.n_soc}</span>
            <h1 className="font-plate font-bold text-4xl mt-1">{v.targa}</h1>
            <p className="text-slate-300 mt-1">{v.tipo}{v.anno ? ` · Anno ${v.anno}` : ""}</p>
          </div>
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${STATO_BADGE[v.stato] || STATO_BADGE.OK}`}>{v.stato}</span>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-5">
          <div className="bg-slate-950/50 rounded-xl p-2.5">
            <Gauge className="w-4 h-4 text-slate-400 mb-1" />
            <div className="text-sm font-plate font-bold">{(v.km || 0).toLocaleString("it-IT")}</div>
            <div className="text-[10px] text-slate-500">km</div>
          </div>
          <div className="bg-slate-950/50 rounded-xl p-2.5">
            <CalendarClock className="w-4 h-4 text-slate-400 mb-1" />
            <div className="text-sm font-semibold">{ultimo}</div>
            <div className="text-[10px] text-slate-500">ult. intervento</div>
          </div>
          <div className="bg-slate-950/50 rounded-xl p-2.5">
            <Activity className="w-4 h-4 text-slate-400 mb-1" />
            <div className="text-sm font-semibold">{(v.reports || []).length}</div>
            <div className="text-[10px] text-slate-500">segnalazioni</div>
          </div>
        </div>
      </div>

      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-6 mb-3">Seleziona cosa vuoi segnalare</p>
      <div className="grid grid-cols-2 gap-3">
        {ACTION_TYPES.map((a) => {
          const Icon = ICONS[a.icon];
          return (
            <button
              key={a.key}
              onClick={() => navigate(`/mezzo/${id}/segnala?tipo=${a.key}`)}
              data-testid={`action-btn-${a.key.replace(/_/g, "-")}`}
              className={`${a.bg} ${a.hover} ${a.text} h-28 rounded-xl p-4 flex flex-col items-start justify-between shadow-md active:scale-95 transition-transform text-left`}
            >
              <Icon className="w-7 h-7" />
              <span className="font-head font-semibold text-sm leading-tight">{a.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
